import {
  CheckboxGroup,
  Colors,
  DateInput,
  DateTimeInput,
  Dropdown,
  EmailInput,
  FileInput,
  HiddenInput,
  MultiDropdown,
  NumberInput,
  PhoneInput,
  RadioGroup,
  TextInput,
  TextInputWithCounter,
  TextReg,
  TimeInput,
  UrlInput,
  type FileResult,
} from '@sunrise-ui/primitives';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { FieldValue, FormFieldRenderConfig } from './types';

type Props = {
  field: FormFieldRenderConfig;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  error?: string;
};

export function DynamicFormField({ field, value, onChange, error }: Props) {
  const {
    id,
    type,
    label,
    placeholder,
    required,
    disabled,
    options = [],
    maxLength,
    minDate,
    maxDate,
    acceptedFileTypes,
    maxFileSizeBytes,
    helperText,
  } = field;

  const hasError = Boolean(error);
  const fieldLabel = required ? `${label} *` : label;

  const renderField = () => {
    switch (type) {
      case 'text':
        return (
          <TextInput
            label={fieldLabel}
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            error={hasError}
            dense={true}
          />
        );

      case 'password':
        return (
          <TextInput
            label={fieldLabel}
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            secureTextEntry
            dense={true}
          />
        );

      case 'email':
        return (
          <EmailInput
            label={fieldLabel}
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            error={hasError}
            dense={true}
          />
        );

      case 'number':
        return (
          <NumberInput
            label={fieldLabel}
            value={String(value ?? '')}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            error={hasError}
            dense={true}
          />
        );

      case 'textarea':
        return (
          <View style={styles.labeledField}>
            <TextReg style={styles.fieldLabel}>{fieldLabel}</TextReg>
            <TextInputWithCounter
              label={placeholder ?? label}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v)}
              maxLength={maxLength ?? 500}
              multiline
              numberOfLines={4}
              fieldError={error ? { message: error } : undefined}
            />
          </View>
        );

      case 'checkbox':
        return (
          <View style={styles.labeledField}>
            <TextReg style={styles.fieldLabel}>{fieldLabel}</TextReg>
            <CheckboxGroup
              options={options}
              selectedValues={(value as string[]) ?? []}
              onValueChange={(v) => onChange(v)}
              disabled={disabled}
            />
          </View>
        );

      case 'radio':
        return (
          <View style={styles.labeledField}>
            <TextReg style={styles.fieldLabel}>{fieldLabel}</TextReg>
            <RadioGroup
              options={options}
              value={(value as string) ?? ''}
              onValueChange={(v) => onChange(v)}
              disabled={disabled}
            />
          </View>
        );

      case 'select':
        return (
          <View style={styles.labeledField}>
            <TextReg style={styles.fieldLabel}>{fieldLabel}</TextReg>
            <Dropdown
              placeholder={placeholder}
              options={options}
              value={(value as string) ?? undefined}
              onChange={(v) => onChange(v)}
            />
          </View>
        );

      case 'multiselect':
        return (
          <View style={styles.labeledField}>
            <TextReg style={styles.fieldLabel}>{fieldLabel}</TextReg>
            <MultiDropdown
              placeholder={placeholder}
              options={options}
              values={(value as string[]) ?? []}
              onChange={(v) => onChange(v)}
            />
          </View>
        );

      case 'date':
        return (
          <DateInput
            label={fieldLabel}
            value={value as Date | undefined}
            onChange={(date: Date) => onChange(date)}
            placeholder={placeholder}
            disabled={disabled}
            minimumDate={minDate}
            maximumDate={maxDate}
            error={hasError}
          />
        );

      case 'time':
        return (
          <TimeInput
            label={fieldLabel}
            value={value as Date | undefined}
            onChange={(date: Date) => onChange(date)}
            placeholder={placeholder}
            disabled={disabled}
            error={hasError}
          />
        );

      case 'datetime':
        return (
          <DateTimeInput
            label={fieldLabel}
            value={value as Date | undefined}
            onChange={(date: Date) => onChange(date)}
            placeholder={placeholder}
            disabled={disabled}
            minimumDate={minDate}
            maximumDate={maxDate}
            error={hasError}
          />
        );

      case 'file':
        return (
          <FileInput
            label={fieldLabel}
            value={value as FileResult | null}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            acceptedTypes={acceptedFileTypes}
            maxSizeBytes={maxFileSizeBytes}
            error={hasError}
            helperText={helperText}
          />
        );

      case 'url':
        return (
          <UrlInput
            label={fieldLabel}
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            placeholder={placeholder}
            disabled={disabled}
            error={hasError}
            dense={true}
          />
        );

      case 'tel':
        return (
          <PhoneInput
            label={fieldLabel}
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
            disabled={disabled}
            error={hasError}
            dense={true}
          />
        );

      case 'hidden':
        return (
          <HiddenInput
            value={(value as string) ?? ''}
            onChange={(v) => onChange(v)}
          />
        );

      default:
        return null;
    }
  };

  if (type === 'hidden') {
    return renderField();
  }

  // Text area has it's own error (in line with counter)
  return (
    <View style={styles.fieldContainer} testID={`form-field-${id}`}>
      {renderField()}
      {error && type !== 'textarea' && (
        <TextReg style={styles.errorText}>{error}</TextReg>
      )}
      {helperText && !error && (
        <TextReg style={styles.helperText}>{helperText}</TextReg>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fieldContainer: {
    gap: 4,
  },
  labeledField: {
    gap: 8,
  },
  fieldLabel: {
    fontSize: 14,
    color: Colors.text,
  },
  errorText: {
    fontSize: 12,
    color: Colors.purple1,
    marginTop: 4,
  },
  helperText: {
    fontSize: 12,
    color: Colors.opacity40,
    marginTop: 4,
  },
});
